/**
 * Pricing — Phase 5b implementation, post-feedback rework.
 *
 * Three tabs:
 *   1. Price Lists  — DataTable + detail Sheet (items + dealer assignments)
 *   2. Calculator   — three-column bidirectional pricing tool
 *                     (product picker | cost breakdown | mode-toggle pricing)
 *                     plus a dealer-aware preview strip with tier discount
 *                     and custom-offer override.
 *   3. Settings     — overhead %, employee cost per unit, default margin %.
 *                     The calculator's "Configure overhead %" link jumps
 *                     here directly so users never have to leave the page.
 *
 * Mock-aware: when `useMockData()` is true, reads come from `pricingMock`
 * and `setMockPricingSettings` writes are local; in real mode the page
 * would call InstantDB transactions.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Tags } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../components/PageHeader";
import { useHasPermission } from "../../lib/permissions";
import {
  PricingCalculator,
  type CalculatorProductOption,
  type CalculatorDealerOption,
  type CalculatorDealerPreview,
} from "../../components/pricing/PricingCalculator";
import {
  PriceListForm,
  type PriceListFormValues,
} from "../../components/forms/PriceListForm";
import {
  PricingSettingsForm,
  type PricingSettingsFormValues,
} from "../../components/forms/PricingSettingsForm";
import { useMockData } from "../../lib/mockMode";
import { usePersistentMockState } from "../../lib/usePersistentMockState";
import {
  mockPriceLists,
  mockPriceListItems,
  mockDealerPriceListAssignments,
  resolvePriceMock,
  getMockPricingSettings,
  setMockPricingSettings,
  type MockPriceList,
  type MockPriceListItem,
  type MockDealerPriceListAssignment,
} from "../../lib/pricingMock";
import { mockProducts, type MockProduct } from "../../lib/catalogMock";

import {
  mockDealers,
  mockDealerTiers,
  mockDealerOffers,
  type MockDealer,
  type MockDealerTier,
  type MockDealerOffer,
} from "../../lib/salesMock";

export const Route = createFileRoute("/_app/pricing")({
  component: PricingPage,
});

type Tab = "lists" | "calculator" | "settings";

function PricingPage() {
  const isMock = useMockData();

  // Calculator surfaces landed cost + employee cost + overhead and reverse-
  // engineers margin from price. Anyone without pricing.write (i.e. who
  // can't influence pricing decisions) shouldn't see the cost basis either,
  // because the breakdown is enough to back into "what we paid for it".
  const canSeeCalculator = useHasPermission("pricing.write");
  // Settings tab edits overhead %, default margin, employee cost. Strictly
  // pricing.settings.
  const canSeeSettings = useHasPermission("pricing.settings");

  // Default tab: lists is always available; if a stale URL/state lands on
  // a now-gated tab we fall back to lists transparently.
  const [tab, setTab] = useState<Tab>("lists");
  const safeTab: Tab =
    tab === "calculator" && !canSeeCalculator
      ? "lists"
      : tab === "settings" && !canSeeSettings
        ? "lists"
        : tab;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dealer Pricing"
        description="Dealer tier price lists, per-dealer overrides, and a cost-to-price calculator. Manages what dealers pay — not retail product MRP."
        actions={
          safeTab === "lists" ? (
            <NewPriceListAction isMock={isMock} />
          ) : safeTab === "calculator" ? (
            <Badge variant="neutral">Live preview</Badge>
          ) : null
        }
      />

      <Tabs value={safeTab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="lists">Price Lists</TabsTrigger>
          {canSeeCalculator ? (
            <TabsTrigger value="calculator">Calculator</TabsTrigger>
          ) : null}
          {canSeeSettings ? (
            <TabsTrigger value="settings">Settings</TabsTrigger>
          ) : null}
        </TabsList>
      </Tabs>

      {safeTab === "lists" ? (
        <PriceListsTab />
      ) : safeTab === "calculator" && canSeeCalculator ? (
        <CalculatorTab
          onJumpToSettings={
            canSeeSettings ? () => setTab("settings") : undefined
          }
        />
      ) : safeTab === "settings" && canSeeSettings ? (
        <SettingsTab isMock={isMock} />
      ) : (
        <PriceListsTab />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * "New price list" action — top-right of the lists tab.
 * ───────────────────────────────────────────────────────────── */

function NewPriceListAction({ isMock }: { isMock: boolean }) {
  const [open, setOpen] = useState(false);
  const [dealerTiers] = usePersistentMockState<MockDealerTier[]>(
    "dealerTiers",
    mockDealerTiers,
  );

  function handleSubmit(values: PriceListFormValues) {
    if (isMock) {
      // Mock-only: surface a toast and close. Real wiring would call
      // `useMutation(api.pricing.priceLists.create, ...)`.
      toast.success(`Price list "${values.displayName}" drafted (mock)`);
    }
    setOpen(false);
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> New price list
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New price list</SheetTitle>
            <SheetDescription>
              Items can be added after the header is created.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <PriceListForm
              dealerTiers={dealerTiers.map((t) => ({
                id: t._id,
                displayName: t.displayName,
              }))}
              onSubmit={handleSubmit}
              onCancel={() => setOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab 1 — Price Lists
 * ───────────────────────────────────────────────────────────── */

type PriceListRow = MockPriceList & {
  itemCount: number;
  dealerCount: number;
};

function PriceListsTab() {
  const [priceLists] = usePersistentMockState("priceLists", mockPriceLists);
  const [priceListItems] = usePersistentMockState("priceListItems", mockPriceListItems);
  const [dealerPriceListAssignments] = usePersistentMockState("dealerPriceListAssignments", mockDealerPriceListAssignments);

  const rows: PriceListRow[] = useMemo(
    () =>
      priceLists.map((p) => ({
        ...p,
        itemCount: priceListItems.filter((i) => i.priceListId === p._id).length,
        dealerCount: dealerPriceListAssignments.filter((a) => a.priceListId === p._id && a.isActive).length,
      })),
    [priceLists, priceListItems, dealerPriceListAssignments],
  );

  const [detailRow, setDetailRow] = useState<PriceListRow | null>(null);

  const tiersById = useMemo(
    () => new Map(mockDealerTiers.map((t) => [t._id, t.displayName])),
    [],
  );

  const columns = useMemo<ColumnDef<PriceListRow>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => (
          <span className="font-mono text-[12px]">{row.original.code}</span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "displayName",
        header: "Name",
        cell: ({ row }) => (
          <span className="font-medium text-(--text-primary)">
            {row.original.displayName}
          </span>
        ),
      },
      {
        id: "tier",
        header: "Default tier",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.defaultForDealerTierId
              ? tiersById.get(row.original.defaultForDealerTierId) ?? "—"
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        cell: ({ row }) => (
          <Badge variant="neutral">{row.original.currency}</Badge>
        ),
      },
      {
        id: "items",
        header: "Items",
        cell: ({ row }) => <span>{row.original.itemCount}</span>,
        meta: { numeric: true },
      },
      {
        id: "dealers",
        header: "Dealers",
        cell: ({ row }) => <span>{row.original.dealerCount}</span>,
        meta: { numeric: true },
      },
      {
        id: "active",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill variant={row.original.isActive ? "success" : "neutral"}>
            {row.original.isActive ? "Active" : "Archived"}
          </StatusPill>
        ),
      },
    ],
    [tiersById],
  );

  return (
    <>
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No price lists"
        emptyDescription="Create your first price list to start pricing dealer orders."
        emptyIcon={<Tags className="h-5 w-5" aria-hidden />}
      />

      <Sheet
        open={detailRow !== null}
        onOpenChange={(o) => {
          if (!o) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow ? <PriceListDetail row={detailRow} /> : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function PriceListDetail({ row }: { row: PriceListRow }) {
  const [priceListItems] = usePersistentMockState("priceListItems", mockPriceListItems);
  const [dealerPriceListAssignments] = usePersistentMockState("dealerPriceListAssignments", mockDealerPriceListAssignments);

  const items = useMemo(() => priceListItems.filter((i) => i.priceListId === row._id), [priceListItems, row._id]);
  const assignments = useMemo(() => dealerPriceListAssignments.filter((a) => a.priceListId === row._id && a.isActive), [dealerPriceListAssignments, row._id]);

  const productsById = useMemo(
    () => new Map(mockProducts.map((p) => [p._id, p])),
    [],
  );
  const dealersById = useMemo(
    () => new Map(mockDealers.map((d) => [d._id, d])),
    [],
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{row.code}</span>
          <StatusPill variant={row.isActive ? "success" : "neutral"}>
            {row.isActive ? "Active" : "Archived"}
          </StatusPill>
        </SheetTitle>
        <SheetDescription>{row.displayName}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Items ({items.length})
            </div>
            <div className="rounded-[var(--radius-md)] border border-(--border-subtle) overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-(--bg-canvas) text-left text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 text-right">Min qty</th>
                    <th className="px-3 py-2 text-right">Unit price</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const product = productsById.get(it.productId);
                    return (
                      <tr
                        key={it._id}
                        className="border-t border-(--border-subtle)"
                      >
                        <td className="px-3 py-2 font-mono text-[12px]">
                          {product?.sku ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-(--text-primary)">
                          {product?.displayName ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {it.minQuantity}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatMoney(it.unitPriceCents, row.currency, {
                            locale: "en-BD",
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Dealer assignments ({assignments.length})
            </div>
            {assignments.length === 0 ? (
              <div className="text-[12px] text-(--text-tertiary)">
                No dealers assigned.
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {assignments.map((a) => {
                  const dealer = dealersById.get(a.dealerId);
                  return (
                    <li
                      key={a._id}
                      className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                    >
                      <span className="text-[13px] text-(--text-primary)">
                        {dealer?.displayName ?? a.dealerId}
                      </span>
                      <Badge variant="neutral">Active</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
        </div>
      </SheetBody>
      <SheetFooter>
        <Button
          variant="outline"
          onClick={() =>
            toast.info("Editing price list items — coming next phase")
          }
        >
          Edit items
        </Button>
        <Button
          variant="outline"
          onClick={() => toast.info("Assign dealer — coming next phase")}
        >
          Assign dealer
        </Button>
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab 2 — Calculator (three-column rework)
 *
 * Reads `mockPricingSettings` to get the live overhead %, employee
 * cost, and default margin so the user's saved values feed through
 * immediately to the breakdown + calculator seed.
 * ───────────────────────────────────────────────────────────── */

function CalculatorTab({
  onJumpToSettings,
}: {
  onJumpToSettings?: () => void;
}) {
  const [productId, setProductId] = useState<string>(mockProducts[0]?._id ?? "");
  const [dealerId, setDealerId] = useState<string | null>(null);

  // Read live settings — re-reads on each render so a save in the
  // Settings tab is reflected here when the user comes back.
  const settings = getMockPricingSettings();

  const product = mockProducts.find((p) => p._id === productId) ?? null;

  // Cost breakdown — applies live overhead % and employee cost.
  const breakdown = useMemo(() => {
    if (!product) {
      return {
        landedCostCents: 0n,
        employeeCostCents: 0n,
        overheadCostCents: 0n,
        totalCostCents: 0n,
      };
    }
    const landed = product.standardCostCents;
    const employee = settings.employeeCostPerUnitBaseCents;
    const overhead =
      (landed * BigInt(settings.overheadBp)) / 10_000n;
    return {
      landedCostCents: landed,
      employeeCostCents: employee,
      overheadCostCents: overhead,
      totalCostCents: landed + employee + overhead,
    };
  }, [product, settings]);

  // Build calculator product/dealer options.
  const productOptions: CalculatorProductOption[] = useMemo(
    () =>
      mockProducts.map((p) => ({
        id: p._id,
        displayName: p.displayName,
        standardCostCents: p.standardCostCents,
      })),
    [],
  );

  const tiersById = useMemo(
    () => new Map(mockDealerTiers.map((t) => [t._id, t])),
    [],
  );

  const dealerOptions: CalculatorDealerOption[] = useMemo(
    () =>
      mockDealers.map((d) => {
        const tier = tiersById.get(d.tierId);
        // tier.discountBp is already in basis points (100bp = 1%).
        const tierBp = tier ? BigInt(tier.discountBp) : 0n;
        return {
          id: d._id,
          displayName: d.displayName,
          tierDisplayName: tier?.displayName ?? null,
          tierExtraDiscountBp: tierBp,
        };
      }),
    [tiersById],
  );

  // Dealer-aware preview: resolve via the existing 5-level mock, then
  // apply tier extra-discount and custom offer overrides.
  const dealerPreview: CalculatorDealerPreview | null = useMemo(() => {
    if (!productId || !dealerId) return null;
    const resolved = resolvePriceMock(dealerId, productId);
    if (resolved.unitPriceCents === 0n) return null;

    const dealerOpt = dealerOptions.find((d) => d.id === dealerId);
    const tierBp = dealerOpt?.tierExtraDiscountBp ?? 0n;
    const tierAdjusted =
      resolved.unitPriceCents -
      (resolved.unitPriceCents * tierBp) / 10_000n;

    const offer = mockDealerOffers.find(
      (o) =>
        o.dealerId === dealerId && o.productId === productId && o.isActive,
    );
    const customOffer = offer ? offer.unitPriceCents : null;

    return {
      baseUnitPriceCents: resolved.unitPriceCents,
      tierDiscountBp: tierBp,
      tierAdjustedUnitPriceCents: tierAdjusted,
      customOfferUnitPriceCents: customOffer,
      sourceLabel: resolved.sourceLabel,
      currency: resolved.currency,
    };
  }, [dealerId, productId, dealerOptions]);

  return (
    <PricingCalculator
      products={productOptions}
      dealers={dealerOptions}
      selectedProductId={productId || null}
      onSelectProduct={setProductId}
      selectedDealerId={dealerId}
      onSelectDealer={setDealerId}
      breakdown={breakdown}
      overheadBp={settings.overheadBp}
      defaultMarginBp={settings.defaultMarginBp}
      dealerPreview={dealerPreview}
      onConfigureSettings={onJumpToSettings}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab 3 — Pricing Settings
 *
 * Singleton form: overhead %, employee cost per unit, default
 * target margin %. Help text under each field explains what it
 * controls and where it gets used. Save updates the live mock
 * settings so the calculator (and `breakdownCosts`) pick
 * the new values up on the next read.
 * ───────────────────────────────────────────────────────────── */

function SettingsTab({ isMock }: { isMock: boolean }) {
  const initial = getMockPricingSettings();

  const [version, setVersion] = useState(0);

  function handleSubmit(values: PricingSettingsFormValues) {
    if (isMock) {
      setMockPricingSettings({
        overheadBp: values.overheadBp,
        employeeCostPerUnitBaseCents: values.employeeCostPerUnitBaseCents,
        defaultMarginBp: values.defaultMarginBp,
      });
      setVersion((v) => v + 1);
      toast.success("Pricing settings saved");
    } else {
      // Real mode would call an InstantDB transaction here.
      toast.info("Pricing settings save — wire to InstantDB when out of mock");
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] text-(--text-secondary) max-w-2xl">
        Workspace-wide defaults used by the calculator and the cost-plus-margin
        fallback for dealer pricing. These values fund the overhead and
        employee-cost lines you see on the Calculator tab.
      </p>
      <PricingSettingsForm
        // Re-key on save so internal field state resets cleanly to saved values.
        key={version}
        initialValues={{
          overheadBp: initial.overheadBp,
          employeeCostPerUnitBaseCents: initial.employeeCostPerUnitBaseCents,
          defaultMarginBp: initial.defaultMarginBp,
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
